import { useCallback, useLayoutEffect, useState } from "react";

/**
 * Computes fixed viewport coordinates for a portal anchored to a trigger element.
 * placement: "bottom" opens below the trigger; "top" opens above it.
 */
export function usePortalPosition(anchorRef, open, { placement = "bottom", gap = 8, align = "start" } = {}) {
  const [style, setStyle] = useState({ visibility: "hidden" });

  const update = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const base = {
      position: "fixed",
      zIndex: 10000,
      visibility: "visible",
    };

    if (placement === "bottom") {
      if (align === "center") {
        setStyle({
          ...base,
          top: rect.bottom + gap,
          left: rect.left + rect.width / 2,
          transform: "translateX(-50%)",
        });
      } else {
        setStyle({
          ...base,
          top: rect.bottom + gap,
          left: rect.left,
          width: rect.width,
        });
      }
      return;
    }

    setStyle({
      ...base,
      top: rect.top - gap,
      left: align === "center" ? rect.left + rect.width / 2 : rect.left,
      transform: align === "center" ? "translate(-50%, -100%)" : "translateY(-100%)",
    });
  }, [anchorRef, placement, gap, align]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle({ visibility: "hidden" });
      return undefined;
    }

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return style;
}
